/* eslint-disable import/prefer-default-export */
import { get, post, put, patch, remove, download } from '@src/services/NetworkUtils'

export const CompanyService = {
  getCompany: async (params) => await get('/v1/company', params),
  getDetailCompany: async (id, params) => await get(`/v1/company/${id}`, params),
  createCompany: async (data) => await post('/v1/company', data),
  updateCompany: async (id, data) => await put(`/v1/company/${id}`, data),
  // Upload a company logo → returns { url }. Persist it via updateCompany({ logo_url }).
  uploadLogo: async (formData) => await post('/v1/company/upload/logo', formData, 'form-data'),
  deleteCompany: async (ids) => await remove('/v1/company', { ids }),
  exportCompany: (params) => download('/v1/company/export', params),
  // CLIK credential activation (View company → Result). `password` must be
  // encrypted with encryptPassword() before calling.
  validateCredential: async (id, data) =>
    await post(`/v1/company/${id}/credentials/validate`, data),
  // Edit + resubmit a REJECTED application (Reject & resend form). Multipart:
  // nib + replacement document files; the BE keeps untouched docs and re-queues.
  reviseCompany: async (id, formData) =>
    await patch(`/v1/company/${id}/revise`, formData, {}, 'form-data'),
  // Company evidence upload (multipart: file + document_type + title).
  // Accepts the four CLIK enrolment types AND anything prefixed "umkm_" —
  // golongan apply() refuses until at least one umkm_ document exists, so this
  // is the call that makes the UMKM application possible at all.
  uploadDocument: async (id, formData) =>
    await post(`/v1/company/${id}/upload/document`, formData, 'form-data'),

  // Blank surat-kuasa template (for the Reject & resend form). Returns an
  // authenticated URL — open it to download.
  suratKuasaTemplate: () => download('/v1/company/enroll/surat-kuasa-template'),

  // ─── Membership agreement (wizard step 4) ─────────────────────────────────
  // Read here, not just in the enrolment wizard: /company has to say WHICH step
  // a company is stuck on, and for step 4 that means knowing whether an
  // agreement is out awaiting signature.
  //
  // 404 "No signing envelope found" is a normal answer, not an error — the BE's
  // findActiveEnvelope excludes voided/declined, so a declined agreement and one
  // never sent are indistinguishable from here. Treat both as "not sent yet".
  //
  // Sequential signing: `status` reads "completed" as soon as the CLIENT has
  // signed; the rest of the lifecycle (CLIK review / countersignature) is in
  // `agreement_status` (see components/agreementStatus.js).
  signingStatus: async (id) => await get(`/v1/company/${id}/enroll/signing-status`),
  // Re-sends the DocuSign email (email mode) / mints a fresh embedded URL.
  resendAgreement: async (id) => await post(`/v1/company/${id}/enroll/refresh-signing-url`),
}

// ─── Load-failure classification ────────────────────────────────────────────
// NetworkUtils never rethrows the axios error itself. When the server answered
// it rethrows `error.response.data` — the backoffice-service envelope; when the
// server never answered it rethrows `{ message: 'Network Error' }`. That
// collapse is what let a dead backend look like an empty account: both cases
// arrive as a rejected promise carrying nothing but a string, so every caller
// that swallowed one fell back to `[]` and rendered a confident "0 item".
//
// The one signal that survives the collapse is the HTTP status. Every JSON body
// core/helpers/request.js emits — success, list, error, notFound — carries a
// numeric `status`. A request that never reached the service (connection
// refused, network changed, DNS, timeout) has no status to carry. Hence the
// rule these helpers exist to state: NO STATUS MEANS NO ANSWER, and nothing may
// be concluded about the data from a failure that produced no answer.

/** HTTP status of an answered request, or null when nothing answered. */
export function httpStatusOf(err) {
  if (!err || typeof err !== 'object') return null
  const raw = err.status ?? err.statusCode ?? err.response?.status
  const n = Number(raw)
  return Number.isFinite(n) && n >= 100 ? n : null
}

/**
 * Did this failure happen before the service could answer?
 *
 * Also true for a gateway that replied with something that is not our envelope
 * (nginx's HTML 502 arrives here as a bare string): from the tenant's side that
 * is the same event — the service is unreachable — and it must be shown as such
 * rather than as a result.
 */
export function isTransportFailure(err) {
  return httpStatusOf(err) === null
}

/**
 * Copy for a failed load. Split by cause because the two failures want
 * different actions from the user: a transport failure is worth retrying now,
 * an answered error usually is not, and the service's own message says why.
 */
export function loadFailureMessage(err) {
  if (isTransportFailure(err)) {
    return 'Cannot connect to the server. Check your connection, then try again.'
  }
  return err?.message || 'Failed to load data. Try again.'
}
